import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import PropTypes from "prop-types";
import axios from "axios";

import SuccessModal from "./SuccessModal";
import "../styles/FeedbackForm.css";

const BASE_URL = process.env.REACT_APP_API_URL;
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;

function BaseModal({
  isOpen,
  onClose,
  title,
  children,
  closeOnBackdrop = true,
}) {
  const containerRef = useRef(null);

  if (!containerRef.current) {
    containerRef.current = document.createElement("div");
    containerRef.current.className = "modal-root-container";
  }

  useEffect(() => {
    if (isOpen) {
      document.body.appendChild(containerRef.current);
      // Prevent background scroll while open
      const { overflow } = document.body.style;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = overflow;
        if (containerRef.current.parentNode) {
          containerRef.current.parentNode.removeChild(containerRef.current);
        }
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || "Feedback dialog"}
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target.classList.contains("modal-backdrop")) {
          onClose?.();
        }
      }}
    >
      <div className="modal-panel" role="document">
        <div className="modal-content">{children}</div>
      </div>
    </div>,
    containerRef.current
  );
}

BaseModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node,
  closeOnBackdrop: PropTypes.bool,
};

const getAuthToken = () => localStorage.getItem("authToken");

const useAxiosConfig = () =>
  useMemo(() => {
    const token = getAuthToken();
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    return { params: { company_code: COMPANY_CODE }, headers };
  }, []);

function SingleItemFeedback({ item, customer_id, onSubmissionComplete }) {
  const [review, setReview] = useState("");
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const axiosConfig = useAxiosConfig();

  const remaining = 255 - review.length;
  // Only rating is required, review is optional
  const isValid = rating > 0 && review.length <= 255;

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isValid || submitting) return;

      const reviewData = {
        company_code: COMPANY_CODE,
        order_id: item?.order_id,
        style_id: item?.style_id,
        style_number: item?.style_number,
        customer_id,
        review: review.trim(),
        rating,
        sku: item?.sku,
      };

      try {
        setSubmitting(true);
        if (!BASE_URL) throw new Error("Missing REACT_APP_API_URL");
        const url = `${BASE_URL.replace(
          /\/$/,
          ""
        )}/api/customer/feedback/reviews`;

        await axios.post(url, reviewData, axiosConfig);

        setReview("");
        setRating(0);
        onSubmissionComplete?.(item?.style_number || item?.style_id);
      } catch (err) {
        console.error("Error submitting review:", err?.response?.data || err);
        setErrorMessage(
          err?.response?.data?.message ||
            "Failed to submit review. Please try again."
        );
        setShowErrorModal(true);
      } finally {
        setSubmitting(false);
      }
    },
    [
      axiosConfig,
      customer_id,
      isValid,
      item,
      rating,
      review,
      submitting,
      onSubmissionComplete,
    ]
  );

  return (
    <form className="feedback-form" onSubmit={handleSubmit}>
      <h4>Leave your feedback for {item?.style_name || item?.style_number}</h4>
      <hr />

      <div className="star-rating" aria-label="Rating">
        {[5, 4, 3, 2, 1].map((star) => (
          <span
            key={star}
            role="button"
            tabIndex={0}
            className={`feedback_star ${star <= rating ? "filled" : "empty"}`}
            onClick={() => setRating(star)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setRating(star);
            }}
            aria-label={`${star} star${star === 1 ? "" : "s"}`}
          >
            ★
          </span>
        ))}
      </div>

      <textarea
        value={review}
        onChange={(e) => setReview(e.target.value)}
        maxLength={255}
        placeholder="Write your review here..."
        aria-label="Your review"
      />
      <div className="feedback-hints">
        <small>{remaining} characters left</small>
      </div>

      <button
        type="submit"
        className="submit-review-btn"
        disabled={!isValid || submitting}
      >
        {submitting ? "Submitting..." : "Submit Your Review"}
      </button>

      <SuccessModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title="Error Submitting Review"
        message={errorMessage}
        variant="error"
      />
    </form>
  );
}

SingleItemFeedback.propTypes = {
  item: PropTypes.shape({
    style_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    style_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    style_name: PropTypes.string,
    order_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sku: PropTypes.string,
  }),
  customer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSubmissionComplete: PropTypes.func,
};

function FeedbackForm({ items, customer_id, onSubmissionComplete, onClose }) {
  const [, setCompletedReviews] = useState([]);

  const handleSubmissionComplete = (styleId) => {
    setCompletedReviews((prev) =>
      prev.includes(styleId) ? prev : [...prev, styleId]
    );
    onSubmissionComplete?.(styleId);
  };

  // Single item case
  if (!Array.isArray(items)) {
    const singleItem = {
      style_id: items?.style_id,
      style_number: items?.style_number,
      style_name: items?.style_name,
      order_id: items?.order_id,
      sku: items?.sku,
    };
    return (
      <div className="feedback-container">
        <SingleItemFeedback
          item={singleItem}
          customer_id={customer_id}
          onSubmissionComplete={handleSubmissionComplete}
        />
      </div>
    );
  }
}

FeedbackForm.propTypes = {
  items: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]),
  customer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSubmissionComplete: PropTypes.func,
  onClose: PropTypes.func,
};

export default function FeedbackModal({
  isOpen,
  onClose,
  items,
  customer_id,
  onSubmissionComplete,
  autoCloseOnComplete = false,
}) {
  const [completedCount, setCompletedCount] = useState(0);
  const total = Array.isArray(items) ? items.length : 1;

  const handleComplete = (styleId) => {
    setCompletedCount((n) => Math.min(total, n + 1));
    onSubmissionComplete?.(styleId);
  };

  useEffect(() => {
    if (autoCloseOnComplete && total > 0 && completedCount >= total) {
      onClose?.();
    }
  }, [autoCloseOnComplete, completedCount, total, onClose]);

  return (
    <BaseModal isOpen={!!isOpen} onClose={onClose}>
      <FeedbackForm
        items={items}
        customer_id={customer_id}
        onSubmissionComplete={handleComplete}
        onClose={onClose}
      />
    </BaseModal>
  );
}

FeedbackModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  items: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]),
  customer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSubmissionComplete: PropTypes.func,
  autoCloseOnComplete: PropTypes.bool,
};

export function openFeedbackModal({
  items,
  customer_id,
  onSubmissionComplete,
  autoCloseOnComplete = false,
} = {}) {
  const mount = document.createElement("div");
  mount.className = "modal-imperative-mount"; // style if desired
  document.body.appendChild(mount);

  const root = createRoot(mount);

  function cleanup() {
    setTimeout(() => {
      try {
        root.unmount();
      } catch {}
      if (mount.parentNode) mount.parentNode.removeChild(mount);
    }, 0);
  }

  return new Promise((resolve) => {
    const handleClose = () => {
      resolve({ closed: true });
      cleanup();
    };

    const handleComplete = (styleId) => {
      onSubmissionComplete?.(styleId);
      if (autoCloseOnComplete) {
        resolve({ completed: true, styleId });
        cleanup();
      }
    };

    root.render(
      <FeedbackModal
        isOpen
        onClose={handleClose}
        items={items}
        customer_id={customer_id}
        onSubmissionComplete={handleComplete}
        autoCloseOnComplete={autoCloseOnComplete}
      />
    );
  });
}
